import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus } from "lucide-react";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/motion";

function renderAnswer(text) {
  return text.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return part;
    return (
      <Link key={i} to={m[2]} className="text-slate-200 underline decoration-hivis/60 underline-offset-4 transition-colors hover:text-hivis">
        {m[1]}
      </Link>
    );
  });
}

export default function FaqPage() {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState(0);

  useEffect(() => { api.get("/faqs").then((r) => setFaqs(r.data)).catch(() => {}); }, []);

  // FAQPage structured data — built from the same fetched FAQ data the page renders, so the
  // JSON-LD text always matches the visible answers (strip the [text](url) markdown links).
  const faqLd = faqs.length > 0 && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1") },
    })),
  };

  return (
    <div data-testid="faq-page">
      {faqLd && <Helmet><script type="application/ld+json">{JSON.stringify(faqLd)}</script></Helmet>}
      <PageHeader
        kicker="FAQs"
        title="Answers, straight up"
        sub="Everything buyers ask about wind ratings, T-posts, tools, bungee life and installation."
        image="/assets/img/w-faq-img-1308.webp"
      />
      <section className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:py-24" data-testid="faq-list">
        {faqs.map((f, i) => (
          <Reveal key={f.q} i={Math.min(i, 3)}>
            <div className="border-b border-girder">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`faq-question-${i}`}
                aria-expanded={open === i}
                className="flex w-full items-center justify-between gap-4 py-6 text-left"
              >
                <span className="font-display text-xl font-bold uppercase tracking-tight text-slate-100 sm:text-2xl">{f.q}</span>
                <motion.span animate={{ rotate: open === i ? 45 : 0 }} transition={{ duration: 0.25 }} className="shrink-0 text-hivis">
                  <Plus className="h-5 w-5" />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="overflow-hidden"
                  >
                    <p className="max-w-3xl pb-6 text-sm leading-relaxed text-slate-400 md:text-base" data-testid={`faq-answer-${i}`}>{renderAnswer(f.a)}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        ))}

        <Reveal className="mt-14">
          <div className="clip-corner border border-girder bg-steel/50 p-8 text-center sm:p-10" data-testid="faq-contact-band">
            <h3 className="font-display text-3xl font-black uppercase text-slate-100">Still have questions?</h3>
            <p className="mt-2 text-sm text-slate-400">Talk to a human in Montrose — 8am to 5pm MST.</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <a href="tel:9706505173" data-testid="faq-call-cta" className="clip-corner-sm bg-hivis px-6 py-3 font-display font-bold uppercase text-obsidian transition-colors hover:bg-hivis-hover">Call 970-650-5173</a>
              <Link to="/contact" data-testid="faq-contact-cta" className="clip-line-sm px-6 py-3 font-display font-bold uppercase text-slate-200 transition-colors hover:text-hivis hover:[--cb-line:#F59E0B]">Send a message</Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
